/**
 * メインレイアウト (タイトルバー + サイドバー + コンテンツ) - Retro-Brutalistic デザインシステム
 */
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/droneOpsApi';

const NAV_ITEMS = [
  { path: '/',     icon: 'calendar_month', label: 'フライト予約', shortLabel: 'Calendar' },
  { path: '/dips', icon: 'link',           label: 'DIPS連携',     shortLabel: 'DIPS', rotate: -45 },
];

const ROLE_LABELS = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

// ユーザーアバター + ドロップダウン
function UserMenu({ email, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initial = email ? email.charAt(0).toUpperCase() : '?';

  return (
    <div className="relative ml-2" ref={ref}>
      {/* アバターボタン */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-[#0068B7] transition-all hover:scale-105 focus:outline-none"
        style={{
          backgroundColor: '#0068b7',
          boxShadow: open ? 'none' : '0 2px 0 0 #00508e',
          transform: open ? 'translateY(2px)' : '',
          fontFamily: 'Space Grotesk, sans-serif',
          fontWeight: 700,
          fontSize: '15px',
          color: '#ffffff',
        }}
        aria-label="ユーザーメニュー"
        aria-expanded={open}
      >
        {initial}
      </button>

      {/* ドロップダウン */}
      {open && (
        <div
          className="absolute right-0 top-12 w-64 bg-white border-2 border-[#0068B7] rounded-xl z-50 overflow-hidden"
          style={{ boxShadow: '4px 4px 0 0 rgba(0,104,183,0.4)' }}
        >
          {/* メールアドレス */}
          <div className="flex items-center gap-3 px-4 py-3">
            <span
              className="material-symbols-outlined flex-shrink-0"
              style={{ fontSize: '18px', color: '#717782' }}
            >
              person
            </span>
            <span
              className="text-sm truncate"
              style={{ color: '#414751', fontFamily: 'Inter, sans-serif' }}
            >
              {email || 'User'}
            </span>
          </div>

          {/* 破線区切り */}
          <div className="border-t-2 border-dashed border-outline-variant mx-3" />

          {/* ログアウト */}
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex items-center gap-3 w-full px-4 py-3 text-on-surface-variant hover:bg-blue-50 hover:translate-x-1 transition-all group"
          >
            <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '20px' }}>
              logout
            </span>
            <span
              className="flex-1 text-left"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600 }}
            >
              ログアウト
            </span>
            <span
              className="material-symbols-outlined opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ fontSize: '16px' }}
            >
              chevron_right
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

// ナビゲーションアイテム（展開時）
function NavItem({ to, icon, label, end, rotate }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          isActive
            ? 'flex items-center gap-3 p-3 rounded-none border-l-4 border-white bg-[#0068B7] text-white shadow-[4px_4px_0_0_rgba(0,0,0,0.2)] transition-all'
            : 'flex items-center gap-3 p-3 rounded-lg text-on-surface-variant hover:translate-x-1 hover:bg-blue-50 transition-all group'
        }
      >
        {({ isActive }) => (
          <>
            <span
              className="material-symbols-outlined flex-shrink-0"
              style={{
                fontSize: '22px',
                fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0",
                ...(rotate != null && { transform: `rotate(${rotate}deg)`, display: 'inline-block' }),
              }}
            >
              {icon}
            </span>
            <span
              className="flex-1 truncate"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600 }}
            >
              {label}
            </span>
            {!isActive && (
              <span
                className="material-symbols-outlined ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ fontSize: '18px' }}
              >
                chevron_right
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

// ナビゲーションアイテム（折りたたみ時）
function NavItemCollapsed({ to, icon, label, end, rotate }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        title={label}
        className={({ isActive }) =>
          isActive
            ? 'flex flex-col items-center justify-center py-3 px-1 border-l-4 border-[#0068B7] bg-blue-50 text-[#0068B7] transition-all'
            : 'flex flex-col items-center justify-center py-3 px-1 rounded-lg text-on-surface-variant hover:bg-blue-50 hover:text-[#0068B7] transition-all'
        }
      >
        {({ isActive }) => (
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: '22px',
              fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0",
              ...(rotate != null && { transform: `rotate(${rotate}deg)`, display: 'inline-block' }),
            }}
          >
            {icon}
          </span>
        )}
      </NavLink>
    </li>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    userApi.me().then(setProfile).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isOrg = profile?.orgType === 'organization';

  return (
    <div
      className="flex flex-col h-screen overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 100%)' }}
    >
      {/* ===== タイトルバー ===== */}
      <header
        className="flex-shrink-0 flex items-center justify-between px-6 h-16 z-50"
        style={{
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '4px solid #0068B7',
          boxShadow: '0 4px 0 0 rgba(0,104,183,0.8)',
        }}
      >
        {/* 左: ハンバーガー + ブランド */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarCollapsed((v) => !v)}
            className="p-2 rounded-full text-[#0068B7] hover:bg-blue-50 active:translate-y-0.5 transition-all"
            aria-label={sidebarCollapsed ? 'メニューを開く' : 'メニューを閉じる'}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>
              {sidebarCollapsed ? 'menu' : 'menu_open'}
            </span>
          </button>
          <span
            className="material-symbols-outlined text-[#0068B7]"
            style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
          >
            rocket_launch
          </span>
          <span
            className="hidden sm:block text-xl tracking-tighter text-[#0068B7]"
            style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800 }}
          >
            AIRGROW FlightPlanner
          </span>
        </div>

        {/* 右: アイコン群 + ユーザーメニュー */}
        <div className="flex items-center gap-1">
          {[
            { icon: 'notifications', label: '通知' },
            { icon: 'help',          label: 'ヘルプ' },
            { icon: 'settings',      label: '設定' },
          ].map(({ icon, label }) => (
            <button
              key={icon}
              title={label}
              className="p-2 rounded-full text-[#0068B7] hover:bg-blue-50 active:translate-y-0.5 transition-all"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>
                {icon}
              </span>
            </button>
          ))}
          <UserMenu email={user?.email} onLogout={handleLogout} />
        </div>
      </header>

      {/* ===== サイドバー + メインコンテンツ ===== */}
      <div className="flex flex-1 overflow-hidden">

        {/* ===== サイドバー ===== */}
        <aside
          className={`
            ${sidebarCollapsed ? 'w-16' : 'w-64'}
            bg-white flex-shrink-0 flex flex-col
            border-r-4 border-[#0068B7] rounded-br-lg
            shadow-[4px_0_0_0_rgba(0,104,183,0.2)]
            transition-all duration-300 z-10
          `}
        >
          {/* ナビゲーション */}
          <nav className="flex-1 pt-4 pb-3 px-2" aria-label="メインナビゲーション">
            <ul className="flex flex-col gap-1">
              {sidebarCollapsed
                ? NAV_ITEMS.map((item) => (
                    <NavItemCollapsed
                      key={item.path}
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      end={item.path === '/'}
                      rotate={item.rotate}
                    />
                  ))
                : NAV_ITEMS.map((item) => (
                    <NavItem
                      key={item.path}
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      end={item.path === '/'}
                      rotate={item.rotate}
                    />
                  ))
              }
            </ul>
          </nav>
        </aside>

        {/* ===== メインコンテンツ ===== */}
        <main className="flex-1 overflow-auto relative">
          {/* ドローンウォーターマーク */}
          <div className="fixed bottom-6 right-6 pointer-events-none select-none opacity-5 z-0">
            <span className="material-symbols-outlined" style={{ fontSize: '120px', color: '#00508e' }}>
              flight
            </span>
          </div>

          <div className="relative z-10 p-6">
            <Outlet />
          </div>
        </main>

      </div>
    </div>
  );
}
